import { now } from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { closeModal, openModalAction } from '../../../actions/modal';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import TabView, { Tab } from '../../../components/TabView';
import Strings from '../../../Strings';
import Cohorts from './Cohorts/Cohorts';
import { actions as cohortActions } from './Cohorts/redux/actions';
import { actions } from './redux/actions';
import {
  ADD_PATIENT,
  DOWNLOAD_NOTES_AND_ACTIONS_RESULT,
  DOWNLOAD_PATIENTS_EVENTS_RESULT,
  PATIENT_LIST,
  PATIENT_LIST_FOR_SUGGESTION,
} from './redux/constants';
import { GET_PROGRAM_ANALYTICS_RESULT, actions as reportActions } from '../Report/redux/actions';
import { CREATE_COHORT } from './Cohorts/redux/constants';
import { executePatientOnboarding } from '../../../modals/_Workflows/OnboardPatient';
import { downloadFileOnly, downloadXlsxFile } from '../../../utils';
import { executeRpmOrgSetup } from '../../../modals/_Workflows/SetupRpmOrg';
import { DATE_FORMAT_YEAR_MONTH_DAY, RPM_PATIENT_STATUS } from '../../../constants';
import { canViewCohorts, hasPermission, PERMISSIONS } from '../../../utils/userPermissions';
import CloudDocuments from '../../CloudDocuments/CloudDocuments';
import PatientsTabs, {
  getStoredPatientViewMode,
  PatientViewMode,
  PatientViewSwitch,
  storePatientViewMode,
} from './PatientsTabs';
import { OnboardStages } from '../../../modals/OnboardPatientWizard/helpers';
import UserList from '../../Users/UserList';
import { CARE_PROVIDER, DOCTOR, isSuperUser } from '../../../utils/userRoles';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import OrgSelect from '../../../components/OrgSelect/OrgSelect';
import BulkUpload from './BulkUpload/BulkUpload';

export const PatientTabs = {
  enrollment: {
    name: Strings.enrollment,
    key: Strings.enrollment,
    path: '/cap-patients/list',
  },
  cohorts: {
    name: Strings.cohorts,
    key: Strings.cohorts,
    path: '/cap-patients/cohorts',
  },
  patientFiles: {
    name: Strings.patientListFiles,
    key: Strings.patientListFiles,
    path: '/cap-patients/patient-list-files',
  },
  careProviders: {
    name: Strings.capPatient.careProviders,
    key: 'care-providers',
    path: '/cap-patients/care-providers',
  },
  bulkUpload: {
    name: Strings.bulkAddPatients,
    key: 'bulk-upload',
    path: '/cap-patients/bulk-upload',
  },
};
function Patients(props) {
  const [cohortsRefreshTimestamp, setCohortsRefreshTimestamp] = useState(now());
  const [patientsRefreshTimestamp, setPatientsRefreshTimestamp] = useState(now());
  const [careProviderRefreshTimestamp, setCareProviderRefreshTimestamp] = useState(now());
  const [refreshEnable, setRefreshEnable] = useState(true);

  const { activeTab, orgIds, userTimezone, nativeRole } = props;
  const viewFromUrl = new URLSearchParams(window.location.search).get('view');
  const initialPatientViewMode = Object.values(PatientViewMode).includes(viewFromUrl)
    ? viewFromUrl
    : getStoredPatientViewMode(props.userId) || PatientViewMode.enrollments;
  const [patientViewMode, setPatientViewMode] = useState(initialPatientViewMode);

  React.useEffect(() => {
    storePatientViewMode(props.userId, patientViewMode);
  }, [props.userId, patientViewMode]);

  const onDownloadBtnClickSuccessHandler = (req, name) => {
    const fileName = req?.response && req?.response?.name ? req?.response?.name : name ? name : moment().toString();
    downloadFileOnly(req, fileName);
  };

  const downloadNotesAndActions = () => {
    const onDateSubmited = dates => {
      const request = {
        includeSignature: true,
        startTimestamp: moment(dates.startDate * 1000).format(),
        endTimestamp: moment(dates.endDate * 1000).format(),
        orgId: orgIds && orgIds.length > 0 ? orgIds[0] : undefined,
      };
      props.downloadNotesAndActions(request).then(resp => {
        if (resp.type === DOWNLOAD_NOTES_AND_ACTIONS_RESULT) {
          onDownloadBtnClickSuccessHandler(
            resp,
            `NoteAll__${moment(request.startTimestamp).format(DATE_FORMAT_YEAR_MONTH_DAY)}_${moment(
              request.endTimestamp,
            ).format(DATE_FORMAT_YEAR_MONTH_DAY)}`,
          );
        }
      });
    };
    props.openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.capPatient.downloadNotesAndActions,
      submitButtonText: Strings.download,
      maxRangeAllowedMonths: 6,
    });
  };

  const downloadPatientEvents = () => {
    const onDateSubmited = dates => {
      const request = {
        startTimestamp: moment(dates.startDate * 1000).format(),
        endTimestamp: moment(dates.endDate * 1000).format(),
        orgId: orgIds && orgIds.length > 0 ? orgIds[0] : undefined,
      };
      props.downloadPatientsEvents(request).then(resp => {
        if (resp.type === DOWNLOAD_PATIENTS_EVENTS_RESULT) {
          onDownloadBtnClickSuccessHandler(
            resp,
            `EventsAll__${moment(request.startTimestamp).format(DATE_FORMAT_YEAR_MONTH_DAY)}_${moment(
              request.endTimestamp,
            ).format(DATE_FORMAT_YEAR_MONTH_DAY)}`,
          );
        }
      });
    };
    props.openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadPatientsEvents,
      submitButtonText: Strings.download,
      startDate: moment().subtract(6, 'months').startOf('day'),
      endDate: moment().endOf('day'),
    });
  };

  const downloadProgramAnalytics = () => {
    const onDateSubmited = dates => {
      const request = {
        startTimestamp: moment(dates.startDate * 1000).format(),
        endTimestamp: moment(dates.endDate * 1000).format(),
        orgId: orgIds && orgIds.length > 0 ? orgIds[0] : undefined,
        // Bucket the report's weeks/months in the user's selected timezone
        // instead of the backend's UTC default.
        timezone: userTimezone || undefined,
      };
      props.getProgramAnalytics(request).then(resp => {
        if (resp.type === GET_PROGRAM_ANALYTICS_RESULT) {
          downloadXlsxFile(
            resp,
            `ProgramAnalytics__${moment(request.startTimestamp).format(DATE_FORMAT_YEAR_MONTH_DAY)}_${moment(
              request.endTimestamp,
            ).format(DATE_FORMAT_YEAR_MONTH_DAY)}`,
          );
        }
      });
    };
    props.openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadProgramAnalytics,
      submitButtonText: Strings.download,
      startDate: moment().subtract(6, 'months').startOf('day'),
      endDate: moment().endOf('day'),
    });
  };

  const isRpmOrgSetup = async () => {
    const conditionsResp = await props.getConditions();
    const facilitiesResp = await props.getFacilities();
    if (conditionsResp?.response?.data?.length > 0 && facilitiesResp?.response?.data?.length) {
      return true;
    }
    return false;
  };

  const getHeaderComponents = () => {
    const options = [];
    if (activeTab === PatientTabs.enrollment.name) {
      options.push({ onSelect: () => props.onBulkAddPatients(() => setPatientsRefreshTimestamp(now())), label: Strings.bulkAddPatients });
      options.push({ onSelect: downloadNotesAndActions, label: Strings.capPatient.downloadNotesAndActions });
      if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DOWNLOAD_VITALS_CSV)) {
        options.push({ onSelect: downloadPatientEvents, label: Strings.downloadPatientsEvents });
      }
      options.push({ onSelect: downloadProgramAnalytics, label: Strings.capPatient.downloadProgramAnalytics });
      options.push({ onSelect: () => props.onSendMessage(props.getPatientsForSuggestion, PATIENT_LIST_FOR_SUGGESTION, now()), label: Strings.sendText });
      if (hasPermission(PERMISSIONS.PATIENTS_RPM_UPLOAD_PATIENT_LIST)) {
        options.push({ onSelect: () => props.onUploadPatientList(), label: Strings.uploadPatientList });
      }
    }
    if (activeTab === PatientTabs.cohorts.name) {
      options.push({ onSelect: () => props.onCreateCohort(() => setCohortsRefreshTimestamp(now())), label: Strings.createCohort });
    }
    if (activeTab === PatientTabs.enrollment.name || activeTab === PatientTabs.cohorts.name) {
      options.push({ onSelect: () => props.onEditConditions(), label: Strings.addEditConditions });
      options.push({ onSelect: () => props.onEditFacilities(), label: Strings.addEditProvidersFacilities });
    }

    const actionsButton = (
      <DropdownMenu
        title={Strings.actions}
        options={options}
      />
    );
    return (
      <>
        {(activeTab === PatientTabs.enrollment.name && <OrgSelect />)}
        {activeTab === PatientTabs.enrollment.name && (
          <>
            <PatientViewSwitch value={patientViewMode} onChange={setPatientViewMode} />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.addPatient}
              onClick={() => props.addPatient(() => setPatientsRefreshTimestamp(now()))}
              testid="add-patient"
            />
          </>
        )}
        {(activeTab === PatientTabs.enrollment.name || activeTab === PatientTabs.cohorts.name) && actionsButton}
        <Button class="refresh" disabled={!refreshEnable} onClick={onRefresh} />
      </>
    );
  };

  const onRefresh = () => {
    setCohortsRefreshTimestamp(now());
    setPatientsRefreshTimestamp(now());
    setRefreshEnable(false);
    setTimeout(() => {
      setRefreshEnable(true);
    }, 10000);
  };

  const urlParams = new URLSearchParams(window.location.search);
  const status = urlParams.get('status') || RPM_PATIENT_STATUS.activated;
  const tabArray = [];
  const patientList = (
    <Tab name={PatientTabs.enrollment.name} key={PatientTabs.enrollment.key} path={PatientTabs.enrollment.path}>
      <PatientsTabs refreshTimestamp={patientsRefreshTimestamp} status={status} viewMode={patientViewMode} />
    </Tab>
  );

  const cohorts = (
    <Tab name={PatientTabs.cohorts.name} key={PatientTabs.cohorts.key} path={PatientTabs.cohorts.path}>
      <Cohorts refreshTimestamp={cohortsRefreshTimestamp} />
    </Tab>
  );

  const patientFiles = (
    <Tab name={PatientTabs.patientFiles.name} key={PatientTabs.patientFiles.key} path={PatientTabs.patientFiles.path}>
      <CloudDocuments patientFiles />
    </Tab>
  );
  const careProviders = (
    <Tab name={PatientTabs.careProviders.name} key={PatientTabs.careProviders.key} path={PatientTabs.careProviders.path}>
      <UserList
        organizationId={props.organizationId}
        role={DOCTOR}
        type={CARE_PROVIDER}
        refreshTimestamp={careProviderRefreshTimestamp} 
      />
    </Tab>
  );

  const bulkUpload = (
    <Tab name={PatientTabs.bulkUpload.name} key={PatientTabs.bulkUpload.key} path={PatientTabs.bulkUpload.path}>
      <BulkUpload refreshTimestamp={patientsRefreshTimestamp} />
    </Tab>
  );

  tabArray.push(patientList);
  canViewCohorts() && tabArray.push(cohorts);
  tabArray.push(patientFiles);
  hasPermission(PERMISSIONS.ORGANIZATION_PROVIDER_VIEW) && tabArray.push(careProviders);
  nativeRole === 'superuser' && tabArray.push(bulkUpload);

  return (
    <>
      <PageHeader left={Strings.enrollAndMonitor} right={getHeaderComponents()} />
      <TabView key="tabs" activeTab={activeTab} routeMode className="" noSwiping>
        {tabArray}
      </TabView>
    </>
  );
}

Patients.propTypes = {
  activeTab: PropTypes.any,
  onBulkAddPatients: PropTypes.func,
  onCreateCohort: PropTypes.func,
  onEditConditions: PropTypes.func,
  onEditFacilities: PropTypes.func,
  downloadNotesAndActions: PropTypes.func,
  downloadPatientsEvents: PropTypes.func,
  onSendMessage: PropTypes.func,
  getPatientsForSuggestion: PropTypes.func,
  getConditions: PropTypes.func,
  getFacilities: PropTypes.func,
  openConfirmModal: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func,
  onUploadPatientList: PropTypes.func,
  addPatient: PropTypes.func,
  closeConfirmationModal: PropTypes.func,
  userId: PropTypes.number,
  userTimezone: PropTypes.string,
  getProgramAnalytics: PropTypes.func,
  organizationId: PropTypes.string,
  orgIds: PropTypes.array,
  nativeRole: PropTypes.string,
};

const mapDispatchToProps = dispatch => ({
  onBulkAddPatients: onSuccess =>
    dispatch(
      openModalAction('bulk-add-patients', {
        actionType: PATIENT_LIST,
        onSuccess,
      }),
    ),
  onCreateCohort: nextAction =>
    dispatch(
      openModalAction('edit-cohort', {
        action: cohortActions.createCohort,
        actionType: CREATE_COHORT,
        nextAction,
      }),
    ),
  onUploadPatientList: () =>
    dispatch(
      openModalAction('bulk-add-patients', {
        uploadPatientListMode: true,
      }),
    ),
  onEditConditions: () => dispatch(openModalAction('edit-conditions')),
  onEditFacilities: () => dispatch(openModalAction('edit-facilities')),
  downloadNotesAndActions: request => dispatch(actions.downloadNotesAndActions(request)),
  downloadPatientsEvents: request => dispatch(actions.downloadPatientsEvents(request)),
  onSendMessage: (loadOptionsAction, loadOptionsActionType, id) =>
    dispatch(
      openModalAction(
        'send-message',
        {
          loadRecipients: loadOptionsAction,
          loadRecipientsActionType: loadOptionsActionType,
        },
        id,
      ),
    ),
  getPatientsForSuggestion: pageRequest => dispatch(actions.getPatientsForSuggestion(pageRequest)),
  getConditions: () => dispatch(cohortActions.getConditions()),
  getFacilities: () => dispatch(cohortActions.getFacilities()),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  closeConfirmationModal: () => dispatch(closeModal('confirmation-modal')),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  addPatient: onSuccess =>
    dispatch(
      openModalAction('onboard-patient', {
        onSuccess,
        stage: OnboardStages.onboarding,
      }),
    ),
  getProgramAnalytics: request => dispatch(reportActions.getProgramAnalytics(request)),
});

const mapStateToProps = state => {
  return {
    labRpmSetupEnabled: state.labState.features.find(f => f.name === 'RPM Setup Workflow')?.selected,
    organizationId: state.auth?.organization?.masked_id,
    orgIds: state.entities?.orgFilter?.orgIds,
    userId: state.auth?.profile?.id,
    userTimezone: state.auth?.profile?.preferences?.timezone,
    nativeRole: state.auth.native_role,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Patients);
